import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

/**
 * Payment failed — a branded outcome page for the Razorpay flow.
 *
 * This is NOT the old PayU return page (which was deleted): that one POSTed to
 * the unauthenticated /api/unauth/paymentHook and marked orders paid on request.
 * This page calls no backend at all. The payment already failed at the gateway;
 * this only tells the customer, cleanly, and offers the way forward.
 *
 * The details arrive from checkout as a router-`state` object — reason, amount,
 * order reference, gateway code — with `order_id` also in the query string, so a
 * refresh keeps at least the order it was about. Nothing here is trusted for
 * money: the order stays unpaid on the server regardless of what this page shows.
 */
@Component({
  selector: 'app-payment-failed',
  templateUrl: './payment-failed.component.html',
})
export class PaymentFailedComponent implements OnInit {
  orderReference = '';
  amount = 0;
  reason = 'Your payment could not be completed.';
  code = '';
  paymentId = '';

  constructor(private route: ActivatedRoute, private router: Router) {
    /*
     * Router `state` is only available synchronously in the constructor via the
     * current navigation — by ngOnInit it is gone. So the rich object is read
     * here; the query param (durable across refresh) is read in ngOnInit.
     */
    const nav = this.router.getCurrentNavigation();
    const payment = (nav?.extras?.state as any)?.payment;
    if (payment) {
      this.orderReference = payment.order_reference || '';
      this.amount = Number(payment.amount) || 0;
      this.reason = payment.reason || this.reason;
      this.code = payment.code || '';
      this.paymentId = payment.payment_id || '';
    }
  }

  /** The Buy Now cart rows, when the failed payment was a Buy Now (from `?items=`). */
  buyNowItems = '';

  ngOnInit() {
    // Durable fallback: a refresh drops router state but keeps the query string.
    const orderId = this.route.snapshot.queryParamMap.get('order_id');
    if (orderId && !this.orderReference) {
      this.orderReference = orderId;
    }
    // Only well-formed cart ids pass; anything else retries the normal checkout.
    const ids = (this.route.snapshot.queryParamMap.get('items') || '')
      .split(',').map(s => Number(s.trim())).filter(n => Number.isInteger(n) && n > 0);
    this.buyNowItems = ids.join(',');
  }

  /**
   * Back to checkout to try again — for the SAME items.
   *
   * The order was left placed-but-unpaid and the cart intact, so checkout simply
   * re-offers "Pay now". A Buy Now goes back to Buy Now with its own rows; the
   * plain checkout would bill the whole cart.
   */
  retry() {
    if (this.buyNowItems) {
      this.router.navigate(['/buy-now'], { queryParams: { items: this.buyNowItems } });
      return;
    }
    this.router.navigate(['/checkout']);
  }

  backToCart() {
    this.router.navigate(['/cart']);
  }
}
